import { useEffect, useState } from "react";
import { Building2, ExternalLink, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { http } from "../../lib/api";
import { fmtTL, fmtDate } from "../../lib/format";
import { go } from "../../lib/notify";
import { canLimitRisk } from "../../lib/roles";
import { useAuth } from "../../context/AuthContext";

const HEADS = ["Banka", "Firma", "Derece", "Ekspertiz Değeri", "İpotek Tutarı", "Toplam Limit", "Toplam Risk", "Vade"];

// Demirbaş → İpotek / Rehin Bilgileri: Limit Risk kartlarındaki İPOTEKLER tablosundan otomatik (kapalı); birden fazla banka alt alta.
// Satıra tıklayınca Limit Risk'teki karta gidilir (vurgulu); ESC ile aynı demirbaş kartına / filtrelere geri dönülür.
export default function DeedMortgages({ assetId, legacy, title = "İpotek Bilgileri" }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const isNew = !assetId || assetId === "new";
  const canGo = canLimitRisk(user);
  const openRecord = (m) => { if (canGo) go(`/veri-girisi?bolum=kredi&alt=limit&kayit=${m.lr_id}&don=__BACK__`); };
  useEffect(() => { if (!isNew) http.get(`/fixed-assets/${assetId}/mortgages`).then((r) => setRows(r.data)).catch(() => {}); }, [assetId, isNew]);
  const legacyText = [legacy?.mortgage_bank, legacy?.mortgage_degree, legacy?.appraisal_value ? `ekspertiz ${fmtTL(legacy.appraisal_value)}` : ""].filter(Boolean).join(" · ");
  return (
    <div className="mt-3 pt-3 border-t border-amber-200/70" data-testid="deed-mortgages">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-amber-900 inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {title} <span className="font-normal normal-case text-slate-500">· Limit Risk girişinden otomatik (kapalı){!isNew && rows.length > 0 && canGo ? " · satıra tıkla → karta git · ESC geri" : ""}</span></span>
        {canGo && <Link to="/veri-girisi?bolum=kredi&alt=limit" className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 hover:underline" data-testid="deed-mortgages-link"><ExternalLink className="w-3 h-3" /> Limit Risk Bilgileri</Link>}
      </div>
      {isNew ? <div className="text-[11px] text-slate-600" data-testid="deed-mortgages-new">İpotek bilgileri (banka, derece, ekspertiz, tutar) Veri Girişi → Limit Risk Bilgileri'nde ilgili firma/banka kartının İPOTEKLER tablosuna bu demirbaş eklenince burada otomatik listelenir.</div>
        : rows.length === 0 ? (
          <div className="text-[11px] text-slate-600" data-testid="deed-mortgages-empty">
            Bu demirbaş için Limit Risk'te ipotek kaydı yok.
            {legacyText && <span className="block mt-1 text-amber-800" data-testid="deed-mortgages-legacy"><ShieldAlert className="w-3 h-3 inline mr-1" />Eski elle giriş: {legacyText} — Limit Risk kartının İPOTEKLER tablosuna ekleyin.</span>}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-amber-200 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-amber-50/70">
                <tr>{HEADS.map((h, i) => <th key={h} className={`px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 font-semibold ${i >= 3 && i <= 6 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((m, i) => (
                  <tr key={`${m.lr_id}-${i}`} className={`border-t border-amber-100 ${canGo ? "cursor-pointer hover:bg-amber-50" : ""}`} data-testid={`deed-mortgage-row-${i}`} onClick={() => openRecord(m)} title={canGo ? "Limit Risk Bilgileri'ndeki karta git (ESC ile geri dön)" : ""}
                    tabIndex={canGo ? 0 : -1} onKeyDown={(e) => { if (canGo && e.key === "Enter") openRecord(m); }}>
                    <td className="px-2 py-1.5 font-semibold" data-testid={`deed-mortgage-bank-${i}`}>{m.bank}</td>
                    <td className="px-2 py-1.5">{m.company}</td>
                    <td className="px-2 py-1.5 font-semibold" data-testid={`deed-mortgage-degree-${i}`}>{m.degree || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono" data-testid={`deed-mortgage-appraisal-${i}`}>{m.appraisal_value ? fmtTL(m.appraisal_value) : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono" data-testid={`deed-mortgage-amount-${i}`}>{m.amount ? fmtTL(m.amount) : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtTL(m.limit)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtTL(m.risk)}</td>
                    <td className="px-2 py-1.5">{m.due_date ? fmtDate(m.due_date) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
